import type { AuthSessionUser } from '@domain/auth/types';
import type { ContactDirectoryUser } from '@domain/contacts/types';
import type { GroupMap } from '@domain/chat/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { UserPresenceIndicator } from '@shared/ui/UserPresenceIndicator';
import { userStatusLabel } from '@shared/utils/ui';
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  SimpleGrid,
  Button,
  Box,
  Alert,
  ScrollArea,
  Checkbox,
} from '@mantine/core';
import { IconRefresh, IconUserPlus, IconUserMinus, IconUsersGroup } from '@tabler/icons-react';

type GroupMemberEditMode = 'add' | 'remove';

type GroupManagementModalsProps = {
  activeGroup?: GroupMap[number];
  currentUser: AuthSessionUser;
  contactsById: Record<number, ContactDirectoryUser>;
  presenceByUserId: Record<number, number>;
  isGroupDetailModalOpen: boolean;
  isGroupMemberModalOpen: boolean;
  groupMemberSubmitting: boolean;
  groupDetailLoading: boolean;
  groupDetailError: string | null;
  groupAddCandidates: ContactDirectoryUser[];
  groupRemoveCandidates: ContactDirectoryUser[];
  groupMemberEditMode: GroupMemberEditMode;
  groupMemberDraftIds: number[];
  setIsGroupDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsGroupMemberModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshGroupDetail: (groupId: number) => Promise<void>;
  handleOpenGroupMemberModal: (mode: GroupMemberEditMode) => void;
  handleToggleGroupMemberDraft: (memberId: number) => void;
  handleSubmitGroupMemberChange: () => Promise<void>;
};

export function GroupManagementModals(props: GroupManagementModalsProps) {
  const {
    activeGroup,
    currentUser,
    contactsById,
    presenceByUserId,
    isGroupDetailModalOpen,
    isGroupMemberModalOpen,
    groupMemberSubmitting,
    groupDetailLoading,
    groupDetailError,
    groupAddCandidates,
    groupRemoveCandidates,
    groupMemberEditMode,
    groupMemberDraftIds,
    setIsGroupDetailModalOpen,
    setIsGroupMemberModalOpen,
    refreshGroupDetail,
    handleOpenGroupMemberModal,
    handleToggleGroupMemberDraft,
    handleSubmitGroupMemberChange,
  } = props;

  return (
    <>
      <Modal
        opened={isGroupDetailModalOpen && !!activeGroup}
        onClose={() => !groupMemberSubmitting && setIsGroupDetailModalOpen(false)}
        title={
          <Group gap="sm">
            <UserAvatar
              id={activeGroup?.id ?? 0}
              name={activeGroup?.name ?? ''}
              fallback="GR"
              size={32}
              avatarKind="group"
            />
            <Stack gap={0}>
              <Text fw={700} size="sm">{activeGroup?.name}</Text>
              <Text size="xs" c="dimmed">群组成员与协作</Text>
            </Stack>
          </Group>
        }
        size="lg"
        centered
        radius="lg"
      >
        {activeGroup && (
          <Stack gap="md">
            <SimpleGrid cols={3} spacing="xs">
              <Paper withBorder p="xs" radius="md" ta="center" bg="var(--mantine-color-body)">
                <Text size="xs" c="dimmed">成员</Text>
                <Text fw={700} size="lg">{activeGroup.memberIds.length}</Text>
              </Paper>
              <Paper withBorder p="xs" radius="md" ta="center" bg="var(--mantine-color-body)">
                <Text size="xs" c="dimmed">提醒</Text>
                <Text fw={700} size="sm" mt={4}>{activeGroup.shieldStatus ? '免打扰' : '正常'}</Text>
              </Paper>
              <Paper withBorder p="xs" radius="md" ta="center" bg="var(--mantine-color-body)">
                <Text size="xs" c="dimmed">身份</Text>
                <Text fw={700} size="sm" mt={4}>已加入</Text>
              </Paper>
            </SimpleGrid>

            <Group grow gap="xs">
              <Button
                variant="light"
                color="terracotta"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void refreshGroupDetail(activeGroup.id)}
                loading={groupDetailLoading}
                disabled={groupMemberSubmitting}
              >
                刷新
              </Button>
              <Button
                variant="light"
                color="red"
                leftSection={<IconUserMinus size={16} />}
                onClick={() => handleOpenGroupMemberModal('remove')}
                disabled={groupMemberSubmitting || groupRemoveCandidates.length === 0}
              >
                移出
              </Button>
              <Button
                variant="filled"
                color="terracotta"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => handleOpenGroupMemberModal('add')}
                disabled={groupMemberSubmitting || groupAddCandidates.length === 0}
              >
                添加
              </Button>
            </Group>

            {groupDetailError && (
              <Alert color="red" variant="light" title="群组信息未更新">
                {groupDetailError}
              </Alert>
            )}

            <Box>
              <Group justify="space-between" mb="xs">
                <Text fw={700} size="sm">成员列表</Text>
                <Text size="xs" c="dimmed">{activeGroup.memberIds.length} 人</Text>
              </Group>
              <ScrollArea.Autosize mah={400} type="scroll">
                <Stack gap={8}>
                  {activeGroup.memberIds.map((memberId) => {
                    const isSelf = memberId === currentUser.id;
                    const contact = contactsById[memberId];
                    const memberName = isSelf
                      ? currentUser.displayName || contact?.name || '我'
                      : contact?.name || '平台用户';
                    const memberSubtitle = isSelf
                      ? contact?.signInfo || '当前登录账号'
                      : contact?.signInfo || '平台用户';

                    return (
                      <Paper key={memberId} withBorder p="sm" radius="md">
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                            <UserAvatar
                              id={memberId}
                              name={memberName}
                              avatarUrl={contactsById[memberId]?.avatarUrl}
                              avatarKind="human"
                              size={36}
                            />
                            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={600} truncate>{memberName}</Text>
                              <Text size="xs" c="dimmed" truncate>{memberSubtitle}</Text>
                            </Stack>
                          </Group>
                          {isSelf ? (
                            <Text size="xs" c="var(--mantine-color-primary-filled)" fw={700}>我</Text>
                          ) : (
                            <Group gap={6} wrap="nowrap">
                              <UserPresenceIndicator
                                status={presenceByUserId[memberId] ?? 0}
                                size={8}
                                borderWidth={0}
                              />
                              <Text size="xs" c="dimmed" fw={600}>
                                {userStatusLabel(presenceByUserId[memberId] ?? 0)}
                              </Text>
                            </Group>
                          )}
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            </Box>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={isGroupMemberModalOpen && !!activeGroup}
        onClose={() => !groupMemberSubmitting && setIsGroupMemberModalOpen(false)}
        title={<Text fw={900}>{groupMemberEditMode === 'add' ? '添加成员' : '移出成员'}</Text>}
        size="md"
        centered
        radius="lg"
      >
        {activeGroup && (
          <Stack gap="md">
            <Paper
              p="md"
              radius="md"
              style={{
                background: 'linear-gradient(135deg, rgba(253, 236, 232, 0.82), rgba(255,255,255,0.94))',
                border: '1px solid var(--locs-border-faint)',
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <IconUsersGroup size={24} color="var(--mantine-color-terracotta-7)" />
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={850} size="sm" truncate>{activeGroup.name}</Text>
                  <Text size="xs" c="dimmed">
                    {groupMemberEditMode === 'add'
                      ? '从已有关系中邀请适合这个主题的人'
                      : '保持群组成员列表整洁'}
                  </Text>
                </Stack>
              </Group>
            </Paper>
            <Box>
              <Text size="xs" c="dimmed" mb="xs">
                {groupMemberEditMode === 'add'
                  ? `向 ${activeGroup.name} 添加关系（可添加 ${groupAddCandidates.length} 位）`
                  : `从 ${activeGroup.name} 选择要移出的成员（可移出 ${groupRemoveCandidates.length} 位）`}
              </Text>

              <Paper withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
                <ScrollArea mah={320} type="scroll">
                  <Stack gap={0}>
                    {(groupMemberEditMode === 'add' ? groupAddCandidates : groupRemoveCandidates).length === 0 ? (
                      <Box py={40} ta="center">
                        <Text size="sm" c="dimmed">
                          {groupMemberEditMode === 'add' ? '没有可添加的关系' : '没有可移出的成员'}
                        </Text>
                      </Box>
                    ) : (
                      (groupMemberEditMode === 'add' ? groupAddCandidates : groupRemoveCandidates).map((contact, index) => {
                        const checked = groupMemberDraftIds.includes(contact.id);
                        const candidates = groupMemberEditMode === 'add' ? groupAddCandidates : groupRemoveCandidates;
                        return (
                          <Box
                            key={contact.id}
                            component="label"
                            p="sm"
                            style={{
                              cursor: groupMemberSubmitting ? 'not-allowed' : 'pointer',
                              borderBottom: index === candidates.length - 1 ? 'none' : '1px solid var(--locs-border)',
                              backgroundColor: checked ? 'var(--mantine-color-terracotta-0)' : 'transparent',
                            }}
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                <UserAvatar
                                  id={contact.id}
                                  name={contact.name}
                                  avatarUrl={contact.avatarUrl}
                                  avatarKind="human"
                                  size={32}
                                />
                                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
	                                  <Text size="sm" fw={600} truncate>{contact.name || '平台用户'}</Text>
	                                  <Text size="xs" c="dimmed" truncate>{contact.signInfo || '平台用户'}</Text>
                                </Stack>
                              </Group>
                              <Checkbox
                                checked={checked}
                                onChange={() => handleToggleGroupMemberDraft(contact.id)}
                                disabled={groupMemberSubmitting}
                                radius="md"
                              />
                            </Group>
                          </Box>
                        );
                      })
                    )}
                  </Stack>
                </ScrollArea>
              </Paper>
            </Box>

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setIsGroupMemberModalOpen(false)} disabled={groupMemberSubmitting}>
                取消
              </Button>
              <Button
                color={groupMemberEditMode === 'add' ? 'terracotta' : 'red'}
                onClick={() => void handleSubmitGroupMemberChange()}
                loading={groupMemberSubmitting}
                disabled={groupMemberDraftIds.length === 0}
                leftSection={groupMemberEditMode === 'add' ? <IconUserPlus size={16} /> : <IconUserMinus size={16} />}
              >
                {groupMemberEditMode === 'add' ? '确认添加' : '确认移出'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
